<template>
  <!-- Bildirimsel modal: bootstrap-vue 2.23 b-modal'ın kullanılan alt kümesi, aynı BS4 DOM'u
       (div.modal > .modal-dialog > .modal-content > header/div.modal-body/footer). Bootstrap JS yok. -->
  <body-portal>
    <div v-if="isVisible" :id="outerId" :style="{ position: 'absolute', zIndex: zIndex }">
      <div ref="modal"
           :id="id || null"
           class="modal fade show"
           style="display: block;"
           role="dialog"
           aria-modal="true"
           tabindex="-1"
           :aria-labelledby="hasHeader && !$slots.header ? titleId : null"
           @keydown="onKeydown"
           @mousedown="onMousedown"
           @mouseup="onMouseup">
        <div class="modal-dialog" :class="dialogClasses">
          <div ref="content" class="modal-content" tabindex="-1">
            <header v-if="hasHeader" class="modal-header">
              <slot name="header" :hide="hide" :close="close">
                <h5 :id="titleId" class="modal-title">{{ title }}</h5>
                <button type="button" aria-label="Close" class="close" @click="close()">&times;</button>
              </slot>
            </header>
            <div class="modal-body">
              <slot :hide="hide" :close="close" />
            </div>
            <footer v-if="!hideFooter" class="modal-footer">
              <slot name="footer" :hide="hide" :close="close" :ok="onOk">
                <button v-if="!okOnly" type="button" class="btn btn-secondary" @click="hide('cancel')">{{ cancelTitle }}</button>
                <button type="button" class="btn btn-primary" @click="onOk()">{{ okTitle }}</button>
              </slot>
            </footer>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show"></div>
    </div>
  </body-portal>
</template>

<script>
  import BodyPortal from '../BodyPortal.vue';
  import { lockModalBody, unlockModalBody, pushModal, removeModal, isTopModal, trapTabKey } from '../../../utils/modalService';

  let uid = 0;

  export default {
    name: 'BaseModal',
    components: { BodyPortal },
    emits: ['show', 'shown', 'hide', 'hidden', 'ok'],
    props: {
      id: { type: String, default: null },
      title: { type: String, default: '' },
      // 'sm' | 'md' | 'lg' | 'xl' (bootstrap-vue gibi `modal-<size>`; md BS4'te sınıfsız genişliktir)
      size: { type: String, default: 'md' },
      centered: { type: Boolean, default: false },
      noCloseOnBackdrop: { type: Boolean, default: false },
      noCloseOnEsc: { type: Boolean, default: false },
      hideHeader: { type: Boolean, default: false },
      hideFooter: { type: Boolean, default: false },
      okOnly: { type: Boolean, default: false },
      okTitle: { type: String, default: 'OK' },
      cancelTitle: { type: String, default: 'Cancel' }
    },
    data() {
      uid += 1;
      return {
        isVisible: false,
        zIndex: 1040,
        uid,
        mousedownOutside: false
      };
    },
    computed: {
      baseId() {
        return this.id || `base-modal-${this.uid}`;
      },
      outerId() {
        return `${this.baseId}___BV_modal_outer_`;
      },
      titleId() {
        return `${this.baseId}___BV_modal_title_`;
      },
      hasHeader() {
        return !this.hideHeader;
      },
      dialogClasses() {
        return {
          [`modal-${this.size}`]: !!this.size,
          'modal-dialog-centered': this.centered
        };
      }
    },
    created() {
      this.stackEntry = { onEscape: this.onEscape };
      this.previouslyFocused = null;
    },
    beforeUnmount() {
      if (this.isVisible) this.release(false);
    },
    methods: {
      show() {
        if (this.isVisible) return;
        this.$emit('show');
        this.previouslyFocused = document.activeElement;
        lockModalBody();
        this.zIndex = pushModal(this.stackEntry);
        this.isVisible = true;
        this.$nextTick(() => {
          // Uzun modalda odak kabı kaydırmasın (içerik üstten başlar)
          if (this.$refs.content) this.$refs.content.focus({ preventScroll: true });
          this.$emit('shown');
        });
      },
      /* trigger: 'headerclose' | 'cancel' | 'ok' | 'backdrop' | 'esc' | undefined (programatik),
         bootstrap-vue'daki BvModalEvent.trigger ile aynı adlar. */
      hide(trigger) {
        if (!this.isVisible) return;
        this.$emit('hide', trigger);
        this.release(true);
        this.$nextTick(() => this.$emit('hidden', trigger));
      },
      close() {
        this.hide('headerclose');
      },
      onOk() {
        this.$emit('ok');
        this.hide('ok');
      },
      release(restoreFocus) {
        this.isVisible = false;
        removeModal(this.stackEntry);
        unlockModalBody();
        /* Odak modalı açan öğeye döner (clientapp VueRouterModalComponent). */
        const target = this.previouslyFocused;
        this.previouslyFocused = null;
        if (restoreFocus && target && document.body.contains(target) && typeof target.focus === 'function') {
          target.focus();
        }
      },
      onEscape() {
        if (!this.noCloseOnEsc) this.hide('esc');
      },
      onKeydown(event) {
        if (isTopModal(this.stackEntry)) trapTabKey(event, this.$refs.content);
      },
      isOutsideContent(target) {
        return !!this.$refs.content && !this.$refs.content.contains(target);
      },
      onMousedown(event) {
        this.mousedownOutside = this.isOutsideContent(event.target);
      },
      /* Arka plan tıklaması: basma ve bırakma içeriğin dışında olmalı (bootstrap-vue onClickOut). */
      onMouseup(event) {
        const outside = this.mousedownOutside && this.isOutsideContent(event.target);
        this.mousedownOutside = false;
        if (outside && !this.noCloseOnBackdrop) this.hide('backdrop');
      }
    }
  };
</script>
